/**
 * Text Operations Module
 *
 * This module contains utilities for text-related operations including:
 * - Updating text content by canvas path
 * - Transforming text case (uppercase, lowercase, capitalize)
 * - Parsing styled handlebar text with formatting
 * - Style token parsing for text formatting
 */

import { multiAudioOptions } from "constants/phase";
import { ELEMENT_TYPE } from "constants/ThumbnailEditor";

import { findObjectByPath } from "./form-structure";
import { findParentGroupWithAlignment, layoutGroupWithAlignment } from "./group-operations";
import { isTextType, updateParentGroup } from "./shared-utils";

/**
 * Updates the text content of a text object at the specified path
 * Handles text wrapping, dynamic sizing, vertical centering, and group layouts
 *
 * @param {Object} editorInstance - Editor instance containing canvas
 * @param {string} path - Canvas object path (e.g., "objects.0.objects.1")
 * @param {string} newText - New text content to set
 * @param {Object} styles - Optional styles to apply
 */
export const updateTextByPath = ({
  editorInstance,
  path,
  newText,
  styles = null,
  isCustomText = false,
  populateGroup = false,
}) => {
  if (!editorInstance?.canvas) return;

  const obj = findObjectByPath(editorInstance.canvas, path);

  if (obj && isTextType(obj.type)) {
    // Check if text wrapping is disabled via templatePayload
    const shouldNotWrap = obj.templatePayload?.textWrap === false;

    if (!obj._originalFontSize) {
      obj._originalFontSize = obj.fontSize;
    }

    if (populateGroup && obj._originalFontSize) {
      obj.fontSize = obj._originalFontSize;
    }

    if (shouldNotWrap) {
      // For non-wrapping text: force single line (no line breaks)
      const singleLineText = newText.replace(/\n/g, " ");

      // Calculate the width needed for this text
      const canvas = editorInstance.canvas;
      const ctx = canvas.getContext("2d");

      // Set font context to match the text object
      const fontWeight = obj.fontWeight || "normal";
      const fontStyle = obj.fontStyle || "normal";
      const fontSize = styles?.fontSize || obj.fontSize || 16;
      const fontFamily = obj.fontFamily || "Arial";
      ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;

      // Measure the actual text width
      const metrics = ctx.measureText(singleLineText || " ");
      const textWidth = metrics.width;
      const padding = 5; // Small padding

      // For textbox: set width dynamically to fit content
      // For i-text/text: they auto-resize, just set text
      if (obj.type === "textbox") {
        obj.set({
          text: singleLineText,
          width: textWidth + padding, // Dynamic width based on content
          splitByGrapheme: false, // Don't split by character
        });
      } else {
        obj.set({
          text: singleLineText,
        });
      }

      if (styles) {
        if (isCustomText) {
          obj.set({ styles });
        } else {
          obj.set({ ...styles });
        }
      }

      // Force canvas to update so we get correct dimensions
      editorInstance.canvas.renderAll();

      // Find parent group with alignment and trigger layout
      const parentGroupInfo = findParentGroupWithAlignment(editorInstance.canvas, path);
      const isAutoLayout = obj?.group?.templatePayload?.autoLayout;
      if (parentGroupInfo && isAutoLayout) {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          layoutGroupWithAlignment(editorInstance.canvas, parentGroupInfo.path);
        });
      }
    } else {
      // For regular textboxes: maintain vertical center when text wraps
      // Store or retrieve the original center Y position (when first loaded from template)
      if (!obj._originalCenterY) {
        // First time editing this text - store current center as original
        obj._originalCenterY = obj.top + (obj.height * obj.scaleY) / 2;
      }

      const originalCenterY = obj._originalCenterY;

      // Check if this is an away_team textbox (should expand from left, keeping right edge fixed)
      const isAwayTeam = obj.templatePayload?.key === "away_team";

      // For away_team: store the pinned right position and original width once
      if (isAwayTeam && obj._pinnedRightX === undefined) {
        // Store the right edge position as the pinned position
        obj._pinnedRightX = obj.left + obj.width * obj.scaleX;
        // Store the original width for comparison
        obj._originalWidth = obj.width;
      }

      // Update text
      obj.set({ text: newText });

      if (styles) {
        if (isCustomText) {
          obj.set({ styles });
        } else {
          obj.set({ ...styles });
        }
      }

      // Force text object to recalculate dimensions
      obj.setCoords();
      editorInstance.canvas.renderAll();

      // For away_team: resize width to fit longest word and keep right edge pinned
      if (isAwayTeam && obj._pinnedRightX !== undefined) {
        // Measure the width needed for the longest word (minimum width for wrapping)
        const canvas = editorInstance.canvas;
        const ctx = canvas.getContext("2d");

        const fontWeight = obj.fontWeight || "normal";
        const fontStyle = obj.fontStyle || "normal";
        const fontSize = obj.fontSize || 16;
        const fontFamily = obj.fontFamily || "Arial";
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;

        // Find the longest word (this is the minimum width needed for wrapping)
        const words = newText.split(/\s+/);
        let maxWordWidth = 0;

        for (const word of words) {
          const wordWidth = ctx.measureText(word).width;
          if (wordWidth > maxWordWidth) {
            maxWordWidth = wordWidth;
          }
        }

        // Use the max of original width and longest word width (+ padding)
        const longestWordWithPadding = maxWordWidth + 10;
        const newWidth = Math.max(obj._originalWidth, longestWordWithPadding);

        // Set the new width and adjust left position to keep right edge pinned
        const newLeft = obj._pinnedRightX - newWidth * obj.scaleX;

        obj.set({
          width: newWidth,
          left: newLeft,
        });
        obj.setCoords();
        editorInstance.canvas.renderAll();
      }

      const isTextVerticalCenterAlign = obj.templatePayload?.textVerticalCenterAlign || false;
      if (isTextVerticalCenterAlign) {
        // Calculate new height after text update
        const newHeight = obj.height * obj.scaleY;

        // Adjust top position to keep center point at original position
        const newTop = originalCenterY - newHeight / 2;

        obj.set({ top: newTop });
        obj.setCoords();
      }

      // If this text is inside a parent group, update the parent group
      updateParentGroup(path, editorInstance);

      editorInstance.canvas.renderAll();
    }
  }
};

/**
 * Transforms text to different cases
 * Supports capitalize, uppercase, lowercase, and default (no change)
 *
 * @param {string} _text - Text to transform
 * @param {string} caseType - Case transformation type ("capitalize" | "uppercase" | "lowercase" | "default")
 * @returns {string} Transformed text
 */
export const transformTextCase = (_text, caseType) => {
  let text = _text;

  if (!text) {
    return text;
  }

  if (typeof text !== "string") {
    text = String(text);
  }

  switch (caseType) {
    case "capitalize":
      return text
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    case "uppercase":
      return text.toUpperCase();
    case "lowercase":
      return text.toLowerCase();
    case "default":
    default:
      return text;
  }
};

/**
 * Parses styled handlebar text with formatting
 * Syntax: [style,attributes]{{handlebar_key}}[/] or [style]plain text[/]
 * Example: [bold,italic,#ff0000]{{player_1_name}}[/]
 *
 * @param {string} inputText - Input text with styled handlebar syntax
 * @returns {{ text: string, templatePayloadRanges: Array }} Parsed text and template payload ranges
 */
export const parseStyledHandlebarText = (inputText) => {
  const outputText = [];

  const ranges = [];

  let currentIndex = 0;

  // Matches [style]{{handlebar}}[/] or [style]text[/]

  const regex = /\[(.*?)\]({{.*?}}|[^[]*?)\[\/\]/g;

  let lastIndex = 0;

  for (const match of inputText.matchAll(regex)) {
    const [fullMatch, styleString, content] = match;

    const matchIndex = match.index;

    // Text before match (unstyled)

    const before = inputText.slice(lastIndex, matchIndex);

    if (before) {
      outputText.push(before);

      ranges.push({
        start: currentIndex,

        end: currentIndex + before.length,

        templatePayload: {
          key: "free_text",

          type: ELEMENT_TYPE.TEXT,

          elementType: ELEMENT_TYPE.TEXT,

          value: before,
        },

        styles: {},
      });

      currentIndex += before.length;
    }

    // Parse content (handlebar or plain text)

    const cleanContent = content.trim();

    const isHandlebar = cleanContent.startsWith("{{") && cleanContent.endsWith("}}");

    const styleObj = parseStyleTokens(styleString);

    let value = cleanContent;

    let templatePayload;

    if (isHandlebar) {
      const fullKey = cleanContent.slice(2, -2); // remove {{ }}

      const [key, nameType] = fullKey.split(".");

      templatePayload = {
        key,

        type: ELEMENT_TYPE.TEXT,

        elementType: ELEMENT_TYPE.TEXT,

        value: cleanContent,

        ...(nameType && { nameType }),

        ...(styleObj.textCase && { textCase: styleObj.textCase }),
      };
    } else {
      templatePayload = {
        key: "free_text",

        type: ELEMENT_TYPE.TEXT,

        elementType: ELEMENT_TYPE.TEXT,

        value: cleanContent,

        ...(styleObj.textCase && { textCase: styleObj.textCase }),
      };
    }

    outputText.push(value);

    ranges.push({
      start: currentIndex,

      end: currentIndex + value.length,

      templatePayload,

      styles: styleObj,
    });

    currentIndex += value.length;

    lastIndex = matchIndex + fullMatch.length;
  }

  // Remaining unstyled text after last match

  const after = inputText.slice(lastIndex);

  if (after) {
    outputText.push(after);

    ranges.push({
      start: currentIndex,

      end: currentIndex + after.length,

      templatePayload: {
        key: "free_text",

        type: ELEMENT_TYPE.TEXT,

        elementType: ELEMENT_TYPE.TEXT,

        value: after,
      },

      styles: {},
    });
  }

  return {
    text: outputText.join(""),

    templatePayloadRanges: ranges,
  };
};

/**
 * Parses style tokens from a style string
 * Extracts font weight, style, family, size, color, and text case
 *
 * @param {string} styleString - Comma-separated style tokens (e.g., "bold,italic,#ff0000,Arial,24px")
 * @returns {Object} Parsed style object
 */
const parseStyleTokens = (styleString) => {
  const tokens = styleString.split(",").map((t) => t.trim());

  const styles = {};

  for (const token of tokens) {
    const lower = token.toLowerCase();

    if (["italic", "normal"].includes(lower)) {
      styles.fontStyle = lower;
    } else if (lower.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)) {
      styles.fill = lower;
    } else if (["uppercase", "lowercase", "capitalize"].includes(lower)) {
      styles.textCase = lower;
    } else if (/^\d+$/.test(token)) {
      // Handle numeric font weight strings like "100", "200", etc.
      const fontWeight = parseInt(token, 10);
      if (!isNaN(fontWeight) && fontWeight >= 100 && fontWeight <= 900) {
        styles.fontWeight = fontWeight;
      }
    } else if (
      ["bold", "semibold", "medium", "regular", "light", "thin", "extra-light", "extra-bold", "black"].includes(lower)
    ) {
      switch (lower) {
        case "extra-bold":
          styles.fontWeight = 800;
          break;
        case "black":
          styles.fontWeight = 900;
          break;
        case "bold":
          styles.fontWeight = 700;
          break;
        case "semibold":
          styles.fontWeight = 600;
          break;
        case "medium":
          styles.fontWeight = 500;
          break;
        case "regular":
          styles.fontWeight = 400;
          break;
        case "light":
          styles.fontWeight = 300;
          break;
        case "extra-light":
          styles.fontWeight = 200;
          break;
        case "thin":
          styles.fontWeight = 100;
          break;
        default:
          styles.fontWeight = 400;
          break;
      }
    } else if (token.match(/^\d+px$/i)) {
      // Parse font size - matches numbers with mandatory 'px' suffix
      const fontSize = parseInt(token.replace(/px$/i, ""), 10);
      if (!isNaN(fontSize) && fontSize > 0) {
        styles.fontSize = fontSize;
      }
    } else if (token) {
      styles.fontFamily = token;
    }
  }

  return styles;
};

/**
 * Computes translation options for a team based on available translations and audio options
 *
 * This function processes a team's translation metadata to generate dropdown/select options
 * where each option represents a language/translation variant available for the team.
 * The label is derived from the multiAudioOptions constant, and the value comes from
 * the team's translated name in that language.
 *
 * @param {Object} team - Team object containing metadata with translations
 * @param {Object} team.metaData - Team metadata object
 * @param {Object} team.metaData.translations - Translation mappings by language code
 * @returns {Array<{label: string, value: string}>} Array of translation options with label and value pairs
 */
export const computeTranslations = (team) => {
  const teamTranslations = team?.metaData?.translations || {};

  const options = [];
  for (const [key, translation] of Object.entries(teamTranslations)) {
    const label = multiAudioOptions.find((op) => op?.alpha2 === key)?.English;
    const value = translation?.teamName;
    options.push({ label, value });
  }

  return options;
};

/**
 * Resets cached positioning and sizing values on text objects
 * This forces fresh recalculation on next text update
 * Useful after group dimensions change (e.g., game population with addWithUpdate)
 *
 * @param {Object} canvasObject - Canvas object (text or group) to reset cache for
 */
export const resetTextObjectCache = (canvasObject) => {
  if (!canvasObject) return;

  // Reset cache on text objects
  if (isTextType(canvasObject.type)) {
    delete canvasObject._originalCenterY;
  }

  // Recursively reset cache in groups
  if (canvasObject.type === "group" && canvasObject._objects) {
    canvasObject._objects.forEach((obj) => resetTextObjectCache(obj));
  }
};
